import React from 'react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Building2, Home } from 'lucide-react';
import { UseFormReturn } from 'react-hook-form';
import { CustomerFormType } from './CustomerFormTypes';

interface CustomerTypeToggleProps {
  form: UseFormReturn<CustomerFormType>;
}

export function CustomerTypeToggle({ form }: CustomerTypeToggleProps) {
  const customerType = form.watch('customer_type') || 'residential';
  const isCommercial = customerType === 'commercial';

  const handleTypeChange = (checked: boolean) => {
    const newType = checked ? 'commercial' : 'residential';
    
    if (checked) {
      // Switching TO commercial - migrate residential data to representative fields
      const currentName = form.getValues('name');
      const currentLastName = form.getValues('last_name');
      const currentCellNumbers = form.getValues('cell_numbers');
      const currentEmails = form.getValues('emails');

      // Only migrate if representative fields are empty
      if (!form.getValues('representative_first_name')) {
        form.setValue('representative_first_name', currentName || '');
      }
      if (!form.getValues('representative_last_name')) {
        form.setValue('representative_last_name', currentLastName || '');
      }
      if (!form.getValues('representative_phone')) {
        form.setValue('representative_phone', currentCellNumbers?.[0]?.value || '');
      }
      if (!form.getValues('representative_email')) {
        form.setValue('representative_email', currentEmails?.[0]?.value || '');
      }
    } else {
      // Switching TO residential - migrate representative data back if residential fields are empty
      const repFirstName = form.getValues('representative_first_name');
      const repLastName = form.getValues('representative_last_name');
      const repPhone = form.getValues('representative_phone');
      const businessEmail = form.getValues('business_email');

      if (!form.getValues('name') && repFirstName) {
        form.setValue('name', repFirstName);
      }
      if (!form.getValues('last_name') && repLastName) {
        form.setValue('last_name', repLastName);
      }
      
      const currentCellNumbers = form.getValues('cell_numbers');
      if ((!currentCellNumbers?.[0]?.value) && repPhone) {
        form.setValue('cell_numbers', [{ value: repPhone }]);
      }
      
      const currentEmails = form.getValues('emails');
      if ((!currentEmails?.[0]?.value) && businessEmail) {
        form.setValue('emails', [{ value: businessEmail }]);
      }
    }

    form.setValue('customer_type', newType);
  };

  return (
    <div className="mb-6">
      <div className="flex items-center justify-center gap-4 p-4 rounded-lg border bg-muted/30">
        <div className={`flex items-center gap-2 transition-opacity ${!isCommercial ? 'opacity-100' : 'opacity-50'}`}>
          <Home className="h-4 w-4" />
          <Label className="font-medium cursor-pointer" onClick={() => handleTypeChange(false)}>
            Residential
          </Label>
        </div>
        
        <Switch
          checked={isCommercial}
          onCheckedChange={handleTypeChange}
          aria-label="Toggle between residential and commercial customer"
        />
        
        <div className={`flex items-center gap-2 transition-opacity ${isCommercial ? 'opacity-100' : 'opacity-50'}`}>
          <Building2 className="h-4 w-4" />
          <Label className="font-medium cursor-pointer" onClick={() => handleTypeChange(true)}>
            Commercial
          </Label>
        </div>
      </div>
    </div>
  );
}
